import React, { forwardRef } from 'react'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Slide from '@mui/material/Slide'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import {
  CloseRounded,
  NotificationsActiveRounded,
  NotificationsNoneRounded,
  PowerOffRounded,
  PowerSettingsNewRounded,
  WarningAmberRounded,
} from '@mui/icons-material'
import { NOTIFICATION_KIND_LABELS } from '../../utils'
import {
  sheetPaperSx,
  Grabber,
  PanelHeader,
  PanelTitle,
  PanelHint,
  PanelBody,
  EnableCard,
  EnableText,
  EnableButton,
  DayLabel,
  ItemList,
  Item,
  KindIcon,
  ItemText,
  ItemTop,
  ItemTitle,
  ItemTime,
  ItemMeta,
  NewDot,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
} from './styles'

const KIND_ICONS = {
  on: <PowerSettingsNewRounded />,
  off: <PowerOffRounded />,
  alarm: <WarningAmberRounded />,
  other: <NotificationsNoneRounded />,
}

const SlideUp = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />)
SlideUp.displayName = 'SlideUp'

const DAY_MS = 24 * 60 * 60 * 1000
const startOfDay = (timestamp) => new Date(timestamp).setHours(0, 0, 0, 0)

const dayLabel = (timestamp) => {
  const daysAgo = Math.round((startOfDay(Date.now()) - startOfDay(timestamp)) / DAY_MS)
  if (daysAgo === 0) return 'Hoy'
  if (daysAgo === 1) return 'Ayer'
  return new Date(timestamp).toLocaleDateString('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  })
}

const formatTime = (timestamp) =>
  new Date(timestamp).toLocaleTimeString('es-CO', {
    hour: 'numeric',
    minute: '2-digit',
  })

// El historial ya viene del más nuevo al más viejo: se agrupa por día seguido.
const groupByDay = (history) => history.reduce((groups, item) => {
  const label = dayLabel(item.timestamp)
  const last = groups[groups.length - 1]
  if (last && last.label === label) {
    last.items.push(item)
  } else {
    groups.push({ label, items: [item] })
  }
  return groups
}, [])

function NotificationItem({ item, isNew }) {
  const kind = item.kind || 'other'
  const label = NOTIFICATION_KIND_LABELS[kind]
  return (
    <Item isNew={isNew}>
      <KindIcon kind={kind} aria-hidden>{KIND_ICONS[kind] || KIND_ICONS.other}</KindIcon>
      <ItemText>
        <ItemTop>
          <ItemTitle>{label || item.title}</ItemTitle>
          <ItemTime>{formatTime(item.timestamp)}</ItemTime>
        </ItemTop>
        {/* Con tipo conocido la frase larga sobra: basta con la moto. */}
        <ItemMeta>{label ? item.vehicle || item.body : item.body}</ItemMeta>
      </ItemText>
      {isNew ? <NewDot aria-label="Nueva" /> : null}
    </Item>
  )
}

function NotificationsPanel({
  open,
  onClose,
  history,
  newSince,
  notificationPermission,
  onEnableNotifications,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const canEnable = notificationPermission === 'default'
  const newCount = history.filter((item) => item.timestamp > newSince).length

  const hint = () => {
    if (history.length === 0) return 'Últimos 2 días'
    if (newCount > 0) return `${newCount} ${newCount === 1 ? 'nueva' : 'nuevas'} · últimos 2 días`
    return `${history.length} ${history.length === 1 ? 'aviso' : 'avisos'} · últimos 2 días`
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={isMobile ? SlideUp : undefined}
      PaperProps={{ sx: { ...sheetPaperSx(isMobile), display: 'flex', flexDirection: 'column' } }}
      aria-labelledby="notifications-title"
    >
      {isMobile ? <Grabber aria-hidden /> : null}
      <PanelHeader>
        <div>
          <PanelTitle id="notifications-title" component="h2">Notificaciones</PanelTitle>
          <PanelHint>{hint()}</PanelHint>
        </div>
        <IconButton aria-label="Cerrar" onClick={onClose}>
          <CloseRounded />
        </IconButton>
      </PanelHeader>
      <PanelBody>
        {canEnable ? (
          <EnableCard>
            <EnableText>Recibe estos avisos en este navegador aunque no estés mirando el mapa.</EnableText>
            <EnableButton variant="contained" disableElevation onClick={onEnableNotifications}>
              Activar
            </EnableButton>
          </EnableCard>
        ) : null}
        {history.length === 0 ? (
          <EmptyState>
            <EmptyIcon aria-hidden><NotificationsActiveRounded /></EmptyIcon>
            <EmptyTitle>Todo tranquilo por aquí</EmptyTitle>
            <EmptyText>
              Aquí verás cuándo tu moto se enciende, se apaga o reporta una alarma,
              aunque no te haya llegado el aviso al celular.
            </EmptyText>
          </EmptyState>
        ) : (
          groupByDay(history).map((group) => (
            <section key={group.label} aria-label={group.label}>
              <DayLabel component="h3">{group.label}</DayLabel>
              <ItemList>
                {group.items.map((item) => (
                  <NotificationItem
                    key={item.key}
                    item={item}
                    isNew={item.timestamp > newSince}
                  />
                ))}
              </ItemList>
            </section>
          ))
        )}
      </PanelBody>
    </Dialog>
  )
}

export { NotificationsPanel }
